import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ApiRequestStatus } from '../../../../model/api-request';
import { GlobalActions } from '../../../../model/global-actions';
import appRoutes from '../../../../routing/app-routes';
import {
  clearPrivacyAgreementError,
  resetPrivacyAgreementState,
  setPrivacy,
} from '../../../../store/features/user-access/user-registration/user-registration-slice';
import { privacyAgreement } from '../../../../store/features/user-access/user-registration/user-registration-thunk';
import { messagesForPrivacyAgreementError } from '../../../../store/features/user-access/user-registration/user-registration-utils';
import { useAppSelector } from '../../../redux';

export interface InformativePageLogicCallbacks {
  privacyAgreementRequestStatus: ApiRequestStatus;
  selectedOption: string;
  setSelectedOption: (value: string) => void;
  setPrivacy: () => void;
  handleSubmit: () => void;
  presaVisione: boolean;
  setPresaVisione: (value: boolean) => void;
}

export const privacyAgreementOptionsValues = {
  agree: 'agree',
  disagree: 'disagree',
};

export const useInformativePageLogic = (
  actions: GlobalActions
): InformativePageLogicCallbacks => {
  const { navigate, showToast, closeToast } = actions;

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const [selectedOption, setSelectedOption] = useState<string | undefined>();
  const [presaVisione, setPresaVisione] = useState<boolean>(false);
  const { privacyAgreementRequestStatus, privacyAgreementError } =
    useAppSelector((state) => state.features.userRegistration);

  const handleSubmit = useCallback(() => {
    if (selectedOption === privacyAgreementOptionsValues.agree) {
      dispatch(setPrivacy(true));
    } else if (selectedOption === privacyAgreementOptionsValues.disagree) {
      dispatch(setPrivacy(false));
    }
    dispatch(privacyAgreement());
  }, [selectedOption, dispatch]);

  useEffect(() => {
    if (privacyAgreementRequestStatus === 'succeeded') {
      dispatch(resetPrivacyAgreementState());
      navigate(appRoutes.InsertEmailUserRegistration, { replace: false });
    }
  }, [dispatch, navigate, privacyAgreementRequestStatus]);

  useEffect(() => {
    if (privacyAgreementError) {
      const errorMessages = messagesForPrivacyAgreementError(
        privacyAgreementError
      );
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(clearPrivacyAgreementError());
    }
  }, [closeToast, dispatch, formatMessage, privacyAgreementError, showToast]);

  return {
    privacyAgreementRequestStatus,
    selectedOption,
    setSelectedOption,
    setPrivacy,
    handleSubmit,
    setPresaVisione,
    presaVisione,
  };
};
